import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  FileText,
  Filter,
  Inbox,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LOGO_URL, PRODUCTS } from "@/lib/siteData";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

const statusBadge = (s) => {
  const cfg = {
    new: "bg-indigo-100 text-indigo-700",
    contacted: "bg-amber-100 text-amber-700",
    closed: "bg-emerald-100 text-emerald-700",
  };
  return cfg[s] || "bg-slate-100 text-slate-700";
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, logout, authAxios } = useAuth();
  const api = useMemo(() => authAxios(), [authAxios]);

  const [tab, setTab] = useState("quotes");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // filters
  const [qSearch, setQSearch] = useState("");
  const [qStatus, setQStatus] = useState("all");
  const [qProduct, setQProduct] = useState("all");
  const [qFrom, setQFrom] = useState("");
  const [qTo, setQTo] = useState("");

  const [cSearch, setCSearch] = useState("");
  const [cStatus, setCStatus] = useState("all");
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      /* ignore */
    }
  }, [api]);

  const fetchQuotes = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const params = {};
      if (qSearch) params.search = qSearch;
      if (qStatus !== "all") params.status = qStatus;
      if (qProduct !== "all") params.product = qProduct;
      if (qFrom) params.date_from = qFrom;
      if (qTo) params.date_to = qTo;
      const { data } = await api.get("/admin/quotes", { params });
      setQuotes(data);
    } catch (err) {
      toast.error("Could not load quotes");
    } finally {
      setLoadingQuotes(false);
    }
  }, [api, qSearch, qStatus, qProduct, qFrom, qTo]);

  const fetchContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const params = {};
      if (cSearch) params.search = cSearch;
      if (cStatus !== "all") params.status = cStatus;
      if (cFrom) params.date_from = cFrom;
      if (cTo) params.date_to = cTo;
      const { data } = await api.get("/admin/contacts", { params });
      setContacts(data);
    } catch (err) {
      toast.error("Could not load contacts");
    } finally {
      setLoadingContacts(false);
    }
  }, [api, cSearch, cStatus, cFrom, cTo]);

  useEffect(() => {
    fetchStats();
    fetchQuotes();
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      fetchStats();
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const onContactStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}/status`, { status });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      fetchStats();
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const downloadCsv = async (kind) => {
    try {
      const params = {};
      if (kind === "quotes") {
        if (qSearch) params.search = qSearch;
        if (qStatus !== "all") params.status = qStatus;
        if (qProduct !== "all") params.product = qProduct;
        if (qFrom) params.date_from = qFrom;
        if (qTo) params.date_to = qTo;
      } else {
        if (cSearch) params.search = cSearch;
        if (cStatus !== "all") params.status = cStatus;
        if (cFrom) params.date_from = cFrom;
        if (cTo) params.date_to = cTo;
      }
      const res = await api.get(`/admin/${kind}/export`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const resetQuoteFilters = () => {
    setQSearch("");
    setQStatus("all");
    setQProduct("all");
    setQFrom("");
    setQTo("");
  };
  const resetContactFilters = () => {
    setCSearch("");
    setCStatus("all");
    setCFrom("");
    setCTo("");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="RPG Rasayan Udyog" className="h-10 w-auto" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-base font-bold uppercase tracking-tight text-navy">
                RPG Rasayan · Admin
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Lead Management Console
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex items-center gap-2 text-xs text-slate-600 bg-slate-100 rounded-full px-3 py-1.5">
              <ShieldCheck size={14} className="text-indigo-600" />
              <span data-testid="admin-user-email">{user?.email}</span>
            </span>
            <Link to="/" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-900" data-testid="admin-site-link">
              View Site
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                toast.success("Signed out");
              }}
              data-testid="admin-logout-btn"
              className="font-semibold uppercase tracking-wider text-xs"
            >
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<FileText size={18} />}
            label="Total Quotes"
            value={stats?.quotes?.total ?? "—"}
            testid="stat-quotes-total"
          />
          <StatCard
            icon={<Inbox size={18} />}
            label="New Quotes"
            value={stats?.quotes?.new ?? "—"}
            accent
            testid="stat-quotes-new"
          />
          <StatCard
            icon={<Mail size={18} />}
            label="Total Contacts"
            value={stats?.contacts?.total ?? "—"}
            testid="stat-contacts-total"
          />
          <StatCard
            icon={<Inbox size={18} />}
            label="New Contacts"
            value={stats?.contacts?.new ?? "—"}
            accent
            testid="stat-contacts-new"
          />
        </section>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-white border border-slate-200">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="font-semibold uppercase tracking-wider text-xs">
              Quote Requests
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="font-semibold uppercase tracking-wider text-xs">
              Contact Enquiries
            </TabsTrigger>
          </TabsList>

          {/* QUOTES */}
          <TabsContent value="quotes" className="mt-5 space-y-4">
            <FilterBar
              testidPrefix="quotes"
              search={qSearch}
              onSearch={setQSearch}
              status={qStatus}
              onStatus={setQStatus}
              product={qProduct}
              onProduct={setQProduct}
              dateFrom={qFrom}
              dateTo={qTo}
              onDateFrom={setQFrom}
              onDateTo={setQTo}
              onApply={fetchQuotes}
              onReset={() => {
                resetQuoteFilters();
                setTimeout(fetchQuotes, 0);
              }}
              onExport={() => downloadCsv("quotes")}
              showProduct
            />
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {loadingQuotes ? (
                <div className="p-12 text-center text-slate-500" data-testid="quotes-loading">
                  <Loader2 className="animate-spin mx-auto mb-3 text-indigo-600" size={22} />
                  Loading...
                </div>
              ) : quotes.length === 0 ? (
                <Empty label="No quote requests match the current filters." testid="quotes-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="quotes-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Received</th>
                        <th className="px-4 py-3 font-semibold">Contact</th>
                        <th className="px-4 py-3 font-semibold">Company</th>
                        <th className="px-4 py-3 font-semibold">Product</th>
                        <th className="px-4 py-3 font-semibold">Quantity</th>
                        <th className="px-4 py-3 font-semibold">Message</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {quotes.map((q) => (
                        <tr key={q.id} className="hover:bg-slate-50 align-top" data-testid={`quote-row-${q.id}`}>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(q.submitted_at)}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{q.name}</div>
                            <div className="text-xs text-slate-500">
                              <a href={`mailto:${q.email}`} className="hover:text-indigo-600">{q.email}</a>
                            </div>
                            <div className="text-xs text-slate-500">{q.phone}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{q.company}</td>
                          <td className="px-4 py-3 text-slate-700">{q.product_interest || "—"}</td>
                          <td className="px-4 py-3 text-slate-700">{q.quantity || "—"}</td>
                          <td className="px-4 py-3 text-slate-600 max-w-xs">
                            <span className="line-clamp-2 block">{q.message || "—"}</span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusSelect
                              value={q.status || "new"}
                              onChange={(v) => onQuoteStatus(q.id, v)}
                              testid={`quote-status-${q.id}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="mt-5 space-y-4">
            <FilterBar
              testidPrefix="contacts"
              search={cSearch}
              onSearch={setCSearch}
              status={cStatus}
              onStatus={setCStatus}
              dateFrom={cFrom}
              dateTo={cTo}
              onDateFrom={setCFrom}
              onDateTo={setCTo}
              onApply={fetchContacts}
              onReset={() => {
                resetContactFilters();
                setTimeout(fetchContacts, 0);
              }}
              onExport={() => downloadCsv("contacts")}
            />
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {loadingContacts ? (
                <div className="p-12 text-center text-slate-500" data-testid="contacts-loading">
                  <Loader2 className="animate-spin mx-auto mb-3 text-indigo-600" size={22} />
                  Loading...
                </div>
              ) : contacts.length === 0 ? (
                <Empty label="No contact enquiries match the current filters." testid="contacts-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="contacts-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Received</th>
                        <th className="px-4 py-3 font-semibold">Contact</th>
                        <th className="px-4 py-3 font-semibold">Subject</th>
                        <th className="px-4 py-3 font-semibold">Message</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contacts.map((c) => (
                        <tr key={c.id} className="hover:bg-slate-50 align-top" data-testid={`contact-row-${c.id}`}>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(c.submitted_at)}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500">
                              <a href={`mailto:${c.email}`} className="hover:text-indigo-600">{c.email}</a>
                            </div>
                            {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{c.subject || "—"}</td>
                          <td className="px-4 py-3 text-slate-600 max-w-md">
                            <span className="line-clamp-2 block">{c.message || "—"}</span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusSelect
                              value={c.status || "new"}
                              onChange={(v) => onContactStatus(c.id, v)}
                              testid={`contact-status-${c.id}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const StatCard = ({ icon, label, value, accent = false, testid }) => (
  <div
    className="bg-white border border-slate-100 rounded-xl p-5 relative overflow-hidden"
    data-testid={testid}
  >
    {accent && (
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-30 gradient-accent" />
    )}
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
      <span className={accent ? "text-indigo-600" : "text-slate-400"}>{icon}</span>
      {label}
    </div>
    <p className="font-display text-3xl font-bold text-slate-900 mt-2 leading-none">
      {value}
    </p>
  </div>
);

const Empty = ({ label, testid }) => (
  <div className="p-12 text-center text-slate-500" data-testid={testid}>
    <Inbox className="mx-auto mb-3 text-slate-300" size={28} />
    {label}
  </div>
);

const StatusSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-[140px] h-9" data-testid={testid}>
      <SelectValue>
        <Badge className={`${statusBadge(value)} font-semibold uppercase text-[10px] tracking-wider hover:${statusBadge(value)}`}>
          {value}
        </Badge>
      </SelectValue>
    </SelectTrigger>
    <SelectContent>
      {["new", "contacted", "closed"].map((s) => (
        <SelectItem key={s} value={s} data-testid={`${testid}-opt-${s}`}>
          <span className="uppercase text-xs font-semibold tracking-wider">{s}</span>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const FilterBar = ({
  testidPrefix,
  search,
  onSearch,
  status,
  onStatus,
  product,
  onProduct,
  dateFrom,
  dateTo,
  onDateFrom,
  onDateTo,
  onApply,
  onReset,
  onExport,
  showProduct = false,
}) => (
  <div
    className="bg-white border border-slate-200 rounded-xl p-4"
    data-testid={`${testidPrefix}-filters`}
  >
    <div className="flex items-center gap-2 mb-3">
      <Filter size={14} className="text-indigo-600" />
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-700">
        Filter &amp; Search
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
      <div className="lg:col-span-2 space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Search</Label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Name, email, company, message..."
            className="pl-9"
            data-testid={`${testidPrefix}-search-input`}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Status</Label>
        <Select value={status} onValueChange={onStatus}>
          <SelectTrigger data-testid={`${testidPrefix}-status-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showProduct && (
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Product</Label>
          <Select value={product} onValueChange={onProduct}>
            <SelectTrigger data-testid={`${testidPrefix}-product-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {PRODUCTS.map((p) => (
                <SelectItem key={p.code} value={p.code}>{p.code}</SelectItem>
              ))}
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => onDateFrom(e.target.value)}
          data-testid={`${testidPrefix}-date-from`}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => onDateTo(e.target.value)}
          data-testid={`${testidPrefix}-date-to`}
        />
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-4">
      <Button
        onClick={onApply}
        size="sm"
        className="gradient-accent text-white font-semibold uppercase tracking-wider"
        data-testid={`${testidPrefix}-apply-btn`}
      >
        <Search size={14} className="mr-1.5" /> Apply
      </Button>
      <Button
        onClick={onReset}
        variant="outline"
        size="sm"
        className="font-semibold uppercase tracking-wider"
        data-testid={`${testidPrefix}-reset-btn`}
      >
        <RefreshCw size={14} className="mr-1.5" /> Reset
      </Button>
      <Button
        onClick={onExport}
        variant="outline"
        size="sm"
        className="font-semibold uppercase tracking-wider"
        data-testid={`${testidPrefix}-export-btn`}
      >
        <Download size={14} className="mr-1.5" /> Export CSV
      </Button>
    </div>
  </div>
);
